/* Wallet primitives. Email derivation stays byte-compatible; new passwords require cryptographic randomness. */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.MintoWalletCore = factory();
})(typeof globalThis !== 'undefined' ? globalThis : this, function () {
  'use strict';
  function toSatoshis(value, allowZero = false) {
    const text = String(value).trim();
    if (!/^\d+(?:\.\d{1,8})?$/.test(text)) throw new Error('not-valid-amount');
    const parts = text.split('.');
    const sat = Number(parts[0]) * 100000000 + Number((parts[1] || '').padEnd(8, '0'));
    if (!Number.isSafeInteger(sat) || sat < 0 || (!allowZero && sat === 0)) throw new Error('not-valid-amount');
    return sat;
  }
  function deriveLegacyHex(email, password, sha256) {
    email = email.toLowerCase(); // Do not trim or normalize existing credentials.
    let s = email + '|' + password + '|';
    s += s.length + '|!@' + ((password.length * 7) + email.length) * 7;
    const lower = password.match(/[a-z]+/g) ? password.match(/[a-z]+/g).length : 1;
    const upper = password.match(/[A-Z]+/g) ? password.match(/[A-Z]+/g).length : 1;
    const digits = password.match(/[0-9]+/g) ? password.match(/[0-9]+/g).length : 1;
    s += ((digits + lower) + upper) * password.length + '3571';
    s += (s + '' + s);
    for (let i = 0; i <= 51; i++) s = sha256.update(s).hex();
    return s;
  }
  function createEmailPassword(crypto, secureContext) {
    if (!secureContext || !crypto || typeof crypto.getRandomValues !== 'function') throw new Error('wallet_secure_context');
    // 32 independent, uniform base-32 symbols = 160 bits. Separators add no entropy.
    const alphabet = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
    const bytes = new Uint8Array(32);
    try {
      crypto.getRandomValues(bytes);
      return Array.from(bytes, byte => alphabet[byte & 31]).join('').match(/.{4}/g).join('-');
    } finally { bytes.fill(0); }
  }
  function createRandomKey(bitcoin, network, crypto, secureContext) {
    if (!secureContext || !crypto || typeof crypto.getRandomValues !== 'function') throw new Error('wallet_secure_context');
    return bitcoin.ECPair.makeRandom({ network, rng(size) {
      const bytes = new Uint8Array(size);
      crypto.getRandomValues(bytes);
      const result = bitcoin.Buffer.from(bytes);
      bytes.fill(0);
      return result;
    }});
  }
  function validAddress(bitcoin, network, address) {
    try {
      const decoded = bitcoin.address.fromBech32(address);
      return decoded.prefix === network.bech32 && decoded.version === 0 &&
        [20, 32].includes(decoded.data.length) && !!bitcoin.address.toOutputScript(address, network);
    } catch (_) { return false; }
  }
  function paymentAddress(bitcoin, network, value) {
    if (typeof value !== 'string' || value.length > 512) throw new Error('not-valid-address');
    let address = value.trim();
    if (/^https?:/i.test(address)) {
      let url;
      try { url = new URL(address); } catch (_) { throw new Error('not-valid-address'); }
      if (url.origin !== 'https://minto.day' || url.username || url.password) throw new Error('not-valid-address');
      const clean = /^\/(ar|de|en|es|fr|el|hi|id|it|ja|ko|mt|nl|pl|ps|pt|ru|tr|vi|zh)\/wallet\/send\/([a-zA-Z0-9]{14,90})$/.exec(url.pathname);
      if (clean && !url.search && !url.hash) address = clean[2];
      else {
        const legacy = url.pathname === '/index.php' && url.searchParams.getAll('view').length === 1 && url.searchParams.get('view') === 'wallet' && [...url.searchParams.keys()].every(key => ['view','lang'].includes(key));
        const cleanWallet = /^\/(ar|de|en|es|fr|el|hi|id|it|ja|ko|mt|nl|pl|ps|pt|ru|tr|vi|zh)\/wallet$/.test(url.pathname) && !url.search;
        if ((!legacy && !cleanWallet) || !/^#\/send\/[a-zA-Z0-9]{14,90}$/.test(url.hash)) throw new Error('not-valid-address');
        address = url.hash.slice('#/send/'.length);
      }
    } else if (/^nito:/i.test(address)) {
      // Only the recipient is imported. Never import a fee, amount or automatic action.
      address = address.slice(5).split('?')[0];
    }
    if (!validAddress(bitcoin, network, address)) throw new Error('not-valid-address');
    return address.toLowerCase();
  }
  function paymentLink(bitcoin, network, address, locale = 'en') {
    if (!['ar','de','en','es','fr','el','hi','id','it','ja','ko','mt','nl','pl','ps','pt','ru','tr','vi','zh'].includes(locale)) locale = 'en';
    return 'https://minto.day/' + locale + '/wallet/send/' + paymentAddress(bitcoin, network, address);
  }
  function buildTransaction(bitcoin, network, keys, outputs, fee, utxos) {
    if (!keys || !keys.compressed || !Array.isArray(outputs) || !outputs.length || outputs.length > 50 ||
        !Number.isSafeInteger(fee) || fee <= 0 || !Array.isArray(utxos)) throw new Error('bad-utxo');
    let target = fee;
    for (const output of outputs) {
      if (!validAddress(bitcoin, network, output.address)) throw new Error('not-valid-address');
      if (!Number.isSafeInteger(output.amount) || output.amount <= 0) throw new Error('not-valid-amount');
      target += output.amount;
      if (!Number.isSafeInteger(target)) throw new Error('not-valid-amount');
    }
    const owner = bitcoin.payments.p2wpkh({ pubkey: keys.publicKey, network });
    const expectedScript = owner.output.toString('hex');
    const selected = [], seen = new Set();
    let total = 0;
    for (const utxo of utxos) {
      if (utxo.scriptPubKey !== expectedScript || !/^[a-f0-9]{64}$/i.test(utxo.txid) ||
          !Number.isInteger(utxo.vout) || utxo.vout < 0 || utxo.vout > 0xffffffff) throw new Error('bad-utxo');
      const id = utxo.txid + ':' + utxo.vout;
      if (seen.has(id)) throw new Error('bad-utxo');
      seen.add(id);
      const value = toSatoshis(typeof utxo.amount === 'number' ? utxo.amount.toFixed(8) : utxo.amount);
      total += value;
      if (!Number.isSafeInteger(total)) throw new Error('bad-utxo');
      selected.push({ ...utxo, value });
      if (total >= target) break;
    }
    if (total < target) throw new Error('not-enough-funds');
    const tx = new bitcoin.TransactionBuilder(network);
    tx.setVersion(2);
    selected.forEach(u => tx.addInput(u.txid, u.vout, null, owner.output));
    outputs.forEach(o => tx.addOutput(o.address, o.amount));
    if (total > target) tx.addOutput(owner.address, total - target);
    selected.forEach((u, i) => tx.sign(i, keys, null, null, u.value));
    return tx.build().toHex();
  }
  function inspectTransaction(bitcoin, network, value) {
    const hex = String(value).trim();
    if (!/^[a-f0-9]+$/i.test(hex) || hex.length % 2 || hex.length < 20 || hex.length > 400000) throw new Error('broadcast-failed');
    const tx = bitcoin.Transaction.fromHex(hex);
    if (!tx.ins.length || !tx.outs.length) throw new Error('broadcast-failed');
    return { hex, id: tx.getId(), outputs: tx.outs.map(output => {
      let address;
      try { address = bitcoin.address.fromOutputScript(output.script, network); }
      catch (_) { address = 'Script: ' + output.script.toString('hex'); }
      return { address, amount: output.value };
    }) };
  }
  function formatSatoshis(sats) {
    if (!Number.isSafeInteger(sats) || sats < 0) throw new Error('not-valid-amount');
    const fraction = String(sats % 100000000).padStart(8, '0').replace(/0+$/, '');
    return String(Math.floor(sats / 100000000)) + (fraction ? '.' + fraction : '');
  }
  function maximumAmount(balance, fee, otherAmounts) {
    if (!Number.isSafeInteger(balance) || balance < 0 || !Number.isSafeInteger(fee) || fee <= 0) throw new Error('wallet_balance_unavailable');
    let remaining = balance - fee;
    for (const value of otherAmounts) {
      if (String(value).trim() !== '') remaining -= toSatoshis(value, true);
      if (!Number.isSafeInteger(remaining)) throw new Error('not-valid-amount');
    }
    if (remaining <= 0) throw new Error('not-enough-funds');
    return formatSatoshis(remaining);
  }
  function paymentFee(inputs, outputs, rate) {
    if (!Number.isInteger(inputs) || inputs < 1 || inputs > 5000 || !Number.isInteger(outputs) || outputs < 1 || outputs > 51 || !Number.isSafeInteger(rate) || rate < 0) throw new Error('not-valid-fee');
    const compact = n => n < 253 ? 1 : 3;
    // Upper bound: native SegWit inputs, 73-byte signatures, P2WSH-sized outputs.
    const stripped = 8 + compact(inputs) + 41 * inputs + compact(outputs) + 43 * outputs;
    const vbytes = Math.ceil((stripped * 4 + 2 + 109 * inputs) / 4);
    const fee = Math.max(1000, Math.ceil(vbytes * rate / 1000));
    if (!Number.isSafeInteger(fee)) throw new Error('not-valid-fee');
    return fee;
  }
  function planPayment(utxos, outputs, rate, manualFee = null) {
    if (!Array.isArray(utxos) || !utxos.length) { const error = new Error('not-enough-funds'); error.maximum = 0; throw error; }
    if (!Array.isArray(outputs) || !outputs.length || outputs.length > 50) throw new Error('not-valid-amount');
    if (manualFee !== null && (!Number.isSafeInteger(manualFee) || manualFee < 1000)) throw new Error('small-fee');
    const amounts = utxos.map(u => toSatoshis(typeof u.amount === 'number' ? u.amount.toFixed(8) : u.amount));
    const balance = amounts.reduce((a,b) => a+b,0), wanted = outputs.reduce((sum,o) => sum+o.amount,0);
    if (!Number.isSafeInteger(balance) || !Number.isSafeInteger(wanted) || outputs.some(o => !Number.isSafeInteger(o.amount) || o.amount <= 0)) throw new Error('not-valid-amount');
    const sweepFee = manualFee === null ? paymentFee(utxos.length, outputs.length, rate) : manualFee;
    let total = 0;
    for (let i=0; i<utxos.length; i++) {
      total += amounts[i];
      let fee = manualFee === null ? paymentFee(i+1, outputs.length+1, rate) : manualFee;
      if (total < wanted+fee && total-wanted >= (manualFee === null ? paymentFee(i+1,outputs.length,rate) : manualFee)) fee = total-wanted;
      if (total >= wanted+fee) {
        // Avoid creating a change output below the native SegWit dust threshold.
        if (total-wanted-fee < 294) fee = total-wanted;
        return {fee,utxos:utxos.slice(0,i+1)};
      }
    }
    const error = new Error('not-enough-funds');
    error.maximum = Math.max(0,balance-sweepFee);
    throw error;
  }
  return Object.freeze({ toSatoshis, formatSatoshis, maximumAmount, paymentFee, planPayment, deriveLegacyHex, createEmailPassword, createRandomKey, validAddress, paymentAddress, paymentLink, buildTransaction, inspectTransaction });
});
